import React from "react";
import { Check, Music, Sparkles } from "lucide-react";

export type BadgeType = "NONE" | "VERIFIED" | "OFFICIAL_ARTIST" | "PARTNER" | string;

export interface ChannelBadgeProps {
  badge?: BadgeType;
  isVerified?: boolean;
  channelName?: string;
  channelHandle?: string;
  size?: "xs" | "sm" | "md" | "lg";
  showTooltip?: boolean;
  className?: string;
}

/**
 * Resolves effective badge type based on explicit props or channel identity
 */
export function resolveBadgeType(
  explicitBadge?: BadgeType,
  isVerified?: boolean,
  channelName?: string,
  channelHandle?: string
): "NONE" | "VERIFIED" | "OFFICIAL_ARTIST" | "PARTNER" {
  if (explicitBadge && explicitBadge !== "NONE") {
    const norm = explicitBadge.toUpperCase();
    if (norm === "OFFICIAL_ARTIST" || norm === "ARTIST") return "OFFICIAL_ARTIST";
    if (norm === "PARTNER") return "PARTNER";
    if (norm === "VERIFIED") return "VERIFIED";
  }

  const raw = `${channelHandle || ""} ${channelName || ""}`.toLowerCase();

  if (raw.includes("jovial")) {
    return "OFFICIAL_ARTIST";
  }
  if (raw.includes("gospelhub") || raw.includes("gospel hub")) {
    return "PARTNER";
  }
  if (raw.includes("helixcreator") || raw.includes("helix creator") || isVerified) {
    return "VERIFIED";
  }

  return "NONE";
}

export const ChannelBadge: React.FC<ChannelBadgeProps> = ({
  badge,
  isVerified,
  channelName,
  channelHandle,
  size = "sm",
  showTooltip = true,
  className = "",
}) => {
  const effectiveBadge = resolveBadgeType(badge, isVerified, channelName, channelHandle);

  if (effectiveBadge === "NONE") {
    return null;
  }

  // Dimension helpers
  const sizeClasses = {
    xs: "w-3 h-3 text-[9px]",
    sm: "w-3.5 h-3.5 text-[10px]",
    md: "w-4 h-4 text-xs",
    lg: "w-5 h-5 text-sm",
  }[size];

  const iconSizes = {
    xs: "w-2 h-2",
    sm: "w-2.5 h-2.5",
    md: "w-3 h-3",
    lg: "w-3.5 h-3.5",
  }[size];

  if (effectiveBadge === "OFFICIAL_ARTIST") {
    return (
      <span
        className={`inline-flex items-center justify-center shrink-0 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 p-0.5 shadow-sm select-none ${sizeClasses} ${className}`}
        title={showTooltip ? "Official Artist Channel • Chaîne d'artiste officielle" : undefined}
        aria-label="Official Artist Channel"
      >
        <Music className={`${iconSizes} stroke-[2.5]`} />
      </span>
    );
  }

  if (effectiveBadge === "PARTNER") {
    return (
      <span
        className={`inline-flex items-center justify-center shrink-0 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/40 p-0.5 shadow-sm select-none ${sizeClasses} ${className}`}
        title={showTooltip ? "Verified Partner • Partenaire vérifié" : undefined}
        aria-label="Verified Partner"
      >
        <Sparkles className={`${iconSizes} fill-amber-400 stroke-[1.5]`} />
      </span>
    );
  }

  // Default VERIFIED Checkmark
  return (
    <span
      className={`inline-flex items-center justify-center shrink-0 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 p-0.5 shadow-sm select-none ${sizeClasses} ${className}`}
      title={showTooltip ? "Verified Creator • Créateur vérifié" : undefined}
      aria-label="Verified Creator"
    >
      <Check className={`${iconSizes} stroke-[3]`} />
    </span>
  );
};
